/**
 * SOLID Review
 * S — Strong. Sole job is the audio-route state machine: 4 pure functions (initialAudioRouteState,
 *     applyRouteChange, applyManualMute, clearNotice) that decide mute/override/notice transitions. No
 *     side effects, no DOM/MediaDevices access — that lives in classifyAudioRoute and the infrastructure
 *     adapter that calls this.
 * O — Good. New route outcomes are added as a new switch arm in applyRouteChange; the three existing cases
 *     (headphones/speakers/unknown) are untouched by such an addition.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Four small, single-purpose functions plus one state shape; callers use only what they need.
 * D — Strong. Imports only the sibling `AudioRoute`/`IEntityAudioRouteClassification` types from
 *     classifyAudioRoute.ts (same application/audio module); no framework or infrastructure imports.
 */
// ---------------------------------------------------------------------------
// AUDIO ROUTE TRANSITIONS — pure state machine deciding how the app reacts
// when the classified audio route changes (headphones plugged/unplugged,
// Bluetooth connected/dropped) and how manual mute choices interact with it.
//
// Semantics:
//  - Re-classifying the same route is a referential no-op, so a manual
//    override survives unrelated devicechange events (plugging a webcam
//    must not re-mute a user who chose to unmute on speakers).
//  - A real physical transition resets the override and applies the
//    automatic behavior: headphones → unmute (+ connected notice, except on
//    the very first classification), speakers → mute (+ disconnected notice).
//  - 'unknown' never touches the mute state: we cannot prove anything, so
//    we neither force-mute someone wearing undetectable headphones nor
//    unmute someone who muted on purpose.
// ---------------------------------------------------------------------------

import type { AudioRoute, IEntityAudioRouteClassification } from './classifyAudioRoute';

export type AudioNotice = 'none' | 'disconnected' | 'connected';
type ManualOverride = 'none' | 'forced-muted' | 'forced-unmuted';

export interface IStateAudioRoute {
  readonly route: AudioRoute;
  /** Desired remote-audio mute state. */
  readonly muted: boolean;
  readonly override: ManualOverride;
  readonly notice: AudioNotice;
  /** False until the first classification — suppresses a notice on initial detection of headphones. */
  readonly everClassified: boolean;
}

export function initialAudioRouteState(): IStateAudioRoute {
  return { route: 'unknown', muted: false, override: 'none', notice: 'none', everClassified: false };
}

export function applyRouteChange(
  prev: IStateAudioRoute,
  next: IEntityAudioRouteClassification,
): IStateAudioRoute {
  if (next.route === prev.route) return prev;

  switch (next.route) {
    case 'headphones':
      return {
        route: 'headphones',
        muted: false,
        override: 'none',
        notice: prev.everClassified ? 'connected' : 'none',
        everClassified: true,
      };
    case 'speakers':
      return {
        route: 'speakers',
        muted: true,
        override: 'none',
        notice: 'disconnected',
        everClassified: true,
      };
    case 'unknown':
      return {
        route: 'unknown',
        muted: prev.muted,
        override: prev.override,
        notice: 'none',
        everClassified: true,
      };
  }
}

export function applyManualMute(prev: IStateAudioRoute, muted: boolean): IStateAudioRoute {
  return { ...prev, muted, override: muted ? 'forced-muted' : 'forced-unmuted', notice: 'none' };
}

export function clearNotice(prev: IStateAudioRoute): IStateAudioRoute {
  if (prev.notice === 'none') return prev;
  return { ...prev, notice: 'none' };
}
